import { Button } from "@/components/Primitives/Button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Primitives/Card"
import { Input } from "@/components/Primitives/Input"
import { Label } from "@/components/Primitives/Label"
import { useConfig } from "@/hooks/useConfig"
import { Text } from "@/components/Primitives/Text"
import { useState } from "react"
import { PasswordRequirements } from "@/components/Primitives/PasswordRequirements/passwordRequirements"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"

import { FormErrorDisplay } from "@/components/Primitives/FormErrorDisplay"
import { AuthLayout } from "@/components/Primitives"
import { Eye, EyeClosed } from "lucide-react"

interface ProfileFormProps extends React.ComponentPropsWithoutRef<"div"> {
  fullName: string
  password: string
  onFullNameChange: (fullName: string) => void
  onPasswordChange: (password: string) => void
  onSubmit: () => Promise<void>
  isSubmitting?: boolean
  error?: string
  passwordValidation?: boolean[]
  passwordRequirements?: { label: string }[]
  isPasswordValid?: boolean
  fieldErrors?: {
    fullName?: string
    password?: string
  }
}

export function ProfileForm({
  fullName,
  password,
  onFullNameChange,
  onPasswordChange,
  onSubmit,
  isSubmitting,
  error,
  passwordValidation = [],
  passwordRequirements = [],
  isPasswordValid = true,
  fieldErrors = {},
}: ProfileFormProps) {
  const { t } = useConfig()
  const [showPassword, setShowPassword] = useState(false)
  const fieldLabels = {
    fullName: t("config_sign_up_profile_name_label"),
    password: t("config_sign_up_profile_password_label"),
  }
  return (
    <AuthLayout
      description={t("config_sign_up_profile_description")}
      imageUrl="/images/profile-in-phone.webp"
    >
      <Card className="p-8 md:px-8">
        <CardHeader className="flex flex-col justify-center">
          <CardTitle>
            <Text variant="h3" bold>
              {t("config_sign_up_profile_description_title")}
            </Text>
            <MarkdownRenderer
              variant="small"
              className="text-neutral-900 mt-2"
              content={t("config_sign_up_profile_description")}
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="mt-2">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              onSubmit()
            }}
          >
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="fullName" className="font-semibold">
                  {t("config_sign_up_profile_name_label")}
                </Label>
                <Input
                  id="fullName"
                  type="text"
                  data-testid="fullName"
                  placeholder={t("config_sign_up_profile_name_placeholder")}
                  value={fullName}
                  required
                  error={fieldErrors.fullName}
                  onChange={(event) => {
                    onFullNameChange(event.target.value)
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password" className="font-semibold">
                  {t("config_sign_up_profile_password_label")}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    data-testid="password"
                    placeholder={t(
                      "config_sign_up_profile_password_placeholder",
                    )}
                    value={password}
                    required
                    error={fieldErrors.password}
                    onChange={(event) => {
                      onPasswordChange(event.target.value)
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 px-2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeClosed className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <PasswordRequirements
                  password={password}
                  validation={passwordValidation}
                  requirements={passwordRequirements}
                />
              </div>
            </div>
            <FormErrorDisplay
              fieldErrors={fieldErrors}
              fieldLabels={fieldLabels}
            />
            <Button
              type="submit"
              className="w-full mt-6"
              data-testid="submit"
              disabled={isSubmitting || !isPasswordValid}
            >
              {isSubmitting
                ? t("config_sign_up_profile_button_submitting")
                : t("config_sign_up_profile_button_continue")}
            </Button>
            {error && !Object.values(fieldErrors).some(Boolean) && (
              <Text variant="small" className="text-red-500 mt-2 text-center">
                {error}
              </Text>
            )}
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
